import React, { useCallback, useEffect, useState, useRef, useContext, useMemo } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Paginator } from "primereact/paginator";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { BreadCrumb } from "primereact/breadcrumb";
import { ProgressSpinner } from "primereact/progressspinner";
import { getCountries, updateCountry, createCountry, toggleCountry, deleteCountries } from "../../api/countryApi";
import { getLanguages } from "../../api/languageApi";
import { AuthContext } from "../../Context/AuthContext";
import CountryForm from "./CountryForm";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

export default function Country() {
    const [countries, setCountries] = useState([]);
    const [allLanguages, setAllLanguages] = useState([]);
    const [selected, setSelected] = useState([]);
    const [formVisible, setFormVisible] = useState(false);
    const [editData, setEditData] = useState(null);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [filterValue, setFilterValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const toast = useRef(null);
    const { auth } = useContext(AuthContext);
    const token = auth?.token;

    const items = [{ label: "COUNTRY" }];
    const home = { icon: "pi pi-home", url: "/" };

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [countryRes, langRes] = await Promise.all([
                getCountries(token),
                getLanguages(token)
            ]);

            const countriesData = countryRes.data.data;
            const languages = langRes.data.data;

            setAllLanguages(languages.filter(l => l.is_enabled));

            const countriesWithLanguages = countriesData.map(c => ({
                ...c,
                Languages: c.Languages || []
            }));

            const isClient = auth?.role === "client";
            setCountries(isClient ? countriesWithLanguages.filter(c => c.is_enabled) : countriesWithLanguages);

        } catch (err) {
            console.error(err);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch data"
            });
        } finally {
            setIsLoading(false);
        }
    }, [token, auth?.role]);

    useEffect(() => {
        if (token) fetchData();
    }, [token, fetchData]);
    
    const textEditor = options => (
        <InputText
            value={options.value}
            onChange={e => options.editorCallback(e.target.value)}
            disabled={!options.rowData.is_enabled}
        />
    );

    const languageEditor = options => {
        const { rowData, value, editorCallback } = options;

        const selectedIds = Array.isArray(value)
            ? value.map(v => (typeof v === "object" ? v.id : v))
            : rowData.Languages?.map(l => l.id) || [];

        return (
            <MultiSelect
                value={selectedIds}
                options={allLanguages.map(l => ({ label: l.name, value: l.id }))}
                onChange={e => {
                    const selectedLanguages = allLanguages.filter(l => e.value.includes(l.id));
                    editorCallback(selectedLanguages);
                }}
                display="chip"
                placeholder="Select Languages"
                disabled={!rowData.is_enabled}
            />
        );
    };

    const onCellEditComplete = e => {
        if (!e.rowData.is_enabled) return;

        const { rowData, newValue, field } = e;

        let formattedValue = newValue;
        if (field === "code") formattedValue = newValue.toLowerCase();
        else if (field === "name") formattedValue = newValue.charAt(0).toUpperCase() + newValue.slice(1).toLowerCase();

        confirmDialog({
            message: "Are you sure you want to update this country?",
            header: "Confirm Update",
            icon: "pi pi-exclamation-triangle",
            accept: async () => {
                setIsLoading(true);
                try {
                    const languageIds = field === "Languages"
                        ? (newValue || []).map(l => l.id)
                        : rowData.Languages.map(l => l.id);

                    await updateCountry(
                        rowData.id,
                        {
                            code: field === "code" ? formattedValue : rowData.code,
                            name: field === "name" ? formattedValue : rowData.name,
                            languageIds
                        },
                        token
                    );

                    await fetchData();
                    toast.current?.show({ severity: "success", summary: "Updated", detail: "Country updated successfully" });
                } catch (err) {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: err.response?.data?.message || "Update failed"
                    });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleDeleteSelected = () => {
        if (!selected.length) return;

        confirmDialog({
            message: `Are you sure you want to delete the selected ${selected.length} country(s)?`,
            header: "Confirm Delete",
            icon: "pi pi-exclamation-triangle",
            accept: async () => {
                setIsLoading(true);
                try {
                    await deleteCountries(selected.map(c => c.id), token);
                    setCountries(prev => prev.filter(c => !selected.some(sel => sel.id === c.id)));
                    setSelected([]);
                    toast.current?.show({ severity: "success", summary: "Deleted", detail: "Selected countries deleted successfully" });
                } catch (err) {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: err?.response?.data?.message || "Delete failed. No countries were deleted."
                    });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleToggleSelected = () => {
        if (!selected.length) return;

        confirmDialog({
            message: "Are you sure you want to toggle status of selected country(s)?",
            header: "Confirm Status Toggle",
            icon: "pi pi-exclamation-triangle",
            accept: async () => {
                setIsLoading(true);
                try {
                    await Promise.all(selected.map(s => toggleCountry(s.id, token)));
                    await fetchData();
                    setSelected([]);
                    toast.current?.show({ severity: "success", summary: "Success", detail: "Status updated successfully" });
                } catch (err) {
                    toast.current?.show({ severity: "error", summary: "Error", detail: err.response?.data?.message || "Operation failed" });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const rowClassName = rowData => ({
        "opacity-50": !rowData.is_enabled,
        "cursor-not-allowed": !rowData.is_enabled
    });

    const filteredCountries = useMemo(() => {
        if (!filterValue) return countries;
        return countries.filter(c =>
            `${c.code} ${c.name}`.toLowerCase().includes(filterValue.toLowerCase())
        );
    }, [countries, filterValue]);

    const countryFilterElement = () => {
        const options = countries.map(c => ({ label: `${c.code} - ${c.name}`, value: `${c.code} ${c.name}` }));
        return (
            <Dropdown
                value={filterValue}
                options={options}
                editable
                placeholder="Search Country"
                onChange={e => setFilterValue(e.value || "")}
                panelStyle={{ maxWidth: "220px" }}
            />
        );
    };

    const renderLanguages = row => (
        <div className="flex flex-wrap gap-1">
            {row.Languages?.map(l => (
                <span key={l.id} style={{ backgroundColor: "#FC5F1CFF", color: "#fff", padding: "2px 8px", borderRadius: "12px", fontSize: "0.85rem" }}>
                    {l.name}
                </span>
            ))}
        </div>
    );

    const downloadJSON = () => {
        const data = selected.length ? selected : countries.filter(c => c.is_enabled);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "countries.json";
        link.click();
    };

    const downloadExcel = () => {
        const data = selected.length ? selected : countries.filter(c => c.is_enabled);
        const worksheet = XLSX.utils.json_to_sheet(
            data.map(d => ({ code: d.code, name: d.name, languages: d.Languages?.map(l => l.name).join(", ") || "" }))
        );
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Countries");
        XLSX.writeFile(workbook, "countries.xlsx");
    };

    const downloadPDF = () => {
        const data = selected.length ? selected : countries.filter(c => c.is_enabled);
        const doc = new jsPDF();
        doc.text("Countries", 14, 20);
        autoTable(doc, {
            head: [["Code", "Name", "Languages"]],
            body: data.map(d => [d.code, d.name, d.Languages?.map(l => l.name).join(", ") || ""]),
            startY: 30
        });
        doc.save("countries.pdf");
    };

    const handleFormSubmit = async (data) => {
        setIsLoading(true);
        try {
            if (editData) {
                await updateCountry(editData.id, data, token);
            } else {
                await createCountry(data, token);
            }
            setFormVisible(false);
            setEditData(null);
            await fetchData();
            toast.current?.show({ severity: "success", summary: "Success", detail: "Operation completed" });
        } catch (err) {
            toast.current?.show({ severity: "error", summary: "Error", detail: err?.response?.data?.message || "Operation failed" });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="p-4" style={{ position: 'relative' }}>
            {isLoading && (
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(255,255,255,0.8)', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ProgressSpinner />
                </div>
            )}
            <Toast ref={toast} />
            <ConfirmDialog />
            <h2 className="mb-1 text-3xl font-bold" style={{ color: "#FC5F1CFF" }}>COUNTRY</h2>
            <BreadCrumb model={items} home={home} style={{ background: "transparent", border: "none" }} />

            <DataTable
                value={filteredCountries.slice(first, first + rows)}
                dataKey="id"
                selection={selected}
                onSelectionChange={e => setSelected(e.value)}
                editMode="cell"
                showGridlines
                rowClassName={rowClassName}
                header={
                    <div className="flex justify-content-end gap-2">
                        <Button icon="pi pi-plus" rounded tooltip="add" onClick={() => setFormVisible(true)} style={{ background: "#FC5F1CFF", border: "none" }} />
                        <Button icon="pi pi-file" rounded tooltip="json" onClick={downloadJSON} style={{ background: "#FC5F1CFF", border: "none" }} />
                        <Button icon="pi pi-file-excel" rounded tooltip="xlsx" onClick={downloadExcel} style={{ background: "#FC5F1CFF", border: "none" }} />
                        <Button icon="pi pi-file-pdf" rounded tooltip="pdf" onClick={downloadPDF} style={{ background: "#FC5F1CFF", border: "none" }} />
                        <Button icon="pi pi-ban" rounded tooltip="disable" disabled={!selected.length} onClick={handleToggleSelected} style={{ background: "#FC5F1CFF", border: "none" }} />
                        <Button icon="pi pi-trash" rounded tooltip="delete" disabled={!selected.length} onClick={handleDeleteSelected} style={{ background: "#FF4B4B", color: "#FFFFFF", border: "none" }} />
                    </div>
                }
                footer={
                    <Paginator
                        first={first}
                        rows={rows}
                        totalRecords={filteredCountries.length}
                        rowsPerPageOptions={[10, 20, 30]}
                        onPageChange={e => { setFirst(e.first); setRows(e.rows); }}
                    />
                }
            >
                <Column selectionMode="multiple" style={{ width: "3rem" }} />
                <Column field="code" header="Code" editor={textEditor} onCellEditComplete={onCellEditComplete} filter filterElement={countryFilterElement} />
                <Column field="name" header="Name" editor={textEditor} onCellEditComplete={onCellEditComplete} filter filterElement={countryFilterElement} />
                <Column field="Languages" header="Languages" editor={languageEditor} onCellEditComplete={onCellEditComplete} body={renderLanguages} />
            </DataTable>

            <CountryForm
                visible={formVisible}
                onHide={() => { setFormVisible(false); setEditData(null); }}
                initialData={editData}
                onSubmit={handleFormSubmit}
                languages={allLanguages}
            />
        </div>
    );
}
